import { FileType, QueryFactory } from '@lmt/shared-types';
import { ClientFactory } from '@lmt/unicorn-shared/app/api/client-factory';
import { getTabId } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { NavigationActions } from '@lmt/unicorn-shared/app/features/navigation/reducer';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { useQueryClient } from '@tanstack/react-query';
import { CustomCommandExecutedEventArgs } from 'devexpress-richedit';

import React from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  DXR,
  RibbonTabType,
  RichEdit,
} from '../../../common/devexpress/RichEdit/dx-richedit';
import { CustomButton } from './CustomButton';
import { openTemplateMenuId } from './OpenTemplatesRichEditMenuItem';
import { getFileNameWithFormat, isDocumentNew } from './richedit-helper';
import { useEventCallback } from '@mui/material';
import { unfocusRichEditor } from './unfocusRichEditor';
import { openOrDownloadFile } from '../../patients/profile/cases/components/documents/common';
import { DocumentFormatApi } from 'devexpress-richedit/lib/client/model-api/formats/enum';

export const readyForPdfButtonId = 'readyForPdfButtonId';

/*
  Component that adds "Ready for PDF" button that finalizes current draft version
  and allows user to print the new version
 */
type Props = {
  richEdit: React.MutableRefObject<RichEdit>;
  tab: RibbonTabType;
  patientId: number;
  dataBlockId: number | undefined;
  documentId: string | undefined;
  groupId: string | undefined;
  templateId: number | undefined;
  onNewDocumentId?: (newId: string) => void;
};

export const ReadyForPdfButton: React.FC<Props> = (props) => {
  const dispatch = useUniDispatch();
  const modals = useModal();
  const queryClient = useQueryClient();
  const i18n = useScopedTranslation('documents');
  const {
    documentId: currentDocumentId,
    groupId,
    onNewDocumentId,
    patientId,
    dataBlockId: blockId,
    templateId,
  } = props;
  const location = useLocation();
  const tabId = getTabId(location);
  const { data: versionHistory } =
    QueryFactory.FileQuery.useGetVersionHistoryQuery(groupId!, {
      enabled: !!groupId,
    });
  const { data: patient } = QueryFactory.PatientQuery.useGetPatientQuery(
    props.patientId!,
    {
      enabled: !!props.patientId,
    },
  );

  const { data: documentTemplateRules } =
    QueryFactory.DocumentTemplateRuleQuery.useGetQuery(
      {
        subtenantId: patient?.subtenantId,
      },
      {
        enabled: !!patient?.subtenantId,
      },
    );

  const handle = useEventCallback(
    async (richEdit: RichEdit, args: CustomCommandExecutedEventArgs) => {
      function setTabHasUnsavedChanges(hasChanges: boolean) {
        if (tabId) {
          dispatch(
            NavigationActions.tabHasUnsavedChangesChanged({
              id: tabId,
              hasUnsavedChanges: hasChanges,
            }),
          );
        }
      }

      richEdit?.exportToBlob(async (data: Blob) => {
        try {
          const isNewDocument = isDocumentNew(richEdit);
          if (isNewDocument) {
            await unfocusRichEditor();
            const newName = await modals.showPrompt({
              defaultValue: '',
              text: '',
              title: i18n.t('save_new_file_as_name_prompt.title'),
              fieldName: i18n.t('save_new_file_as_name_prompt.text'),
              isMandatory: true,
              maxLength: 200,
              validate: (value: string) => {
                if (/[\/\\]/.test(value)) {
                  return i18n.t(
                    'query_builder.errors.file_name_invalid_characters',
                  );
                }

                return true;
              },
            });
            if (!newName) {
              return;
            }
            richEdit.fileName = newName;
          }
          let documentId = currentDocumentId;
          if (richEdit.hasUnsavedChanges || isNewDocument) {
            const file = {
              fileName: getFileNameWithFormat(
                richEdit.fileName,
                DXR.DocumentFormat.OpenXml,
              ),
              data: data,
            };
            // it's important to set it before HTTP calls to prevent
            // Bug 41737: Duplicate draft creating on clicking "Ready for PDF" and switching to another tab
            // (another save happens in SaveDocumentRichEditButton from NavigationActions.setSaveChangesHandler)
            richEdit.hasUnsavedChanges = false;
            try {
              if (currentDocumentId) {
                await ClientFactory.FileClient.updateFile(
                  currentDocumentId,
                  file,
                );
              } else {
                const fileInfo = await ClientFactory.FileClient.create(
                  FileType.DocumentDraft,
                  `Template: ${richEdit.fileName}`,
                  blockId,
                  patientId,
                  null,
                  templateId,
                  null,
                  file,
                );
                documentId = fileInfo.id;
              }
            } catch (e) {
              richEdit.hasUnsavedChanges = true;
              throw e;
            }
            setTabHasUnsavedChanges(false);
            try {
              richEdit.updateRibbon((ribbon) => {
                if (!ribbon) return;
                const newFilePanel = ribbon.getTab(DXR.RibbonTabType.File)!;
                newFilePanel.removeItem(openTemplateMenuId);
              });
            } catch (e) {}
          }
          if (!documentId) {
            return;
          }
          const newVersion =
            await QueryFactory.FileQuery.Client.publishDraft(documentId);
          if (!currentDocumentId) {
            onNewDocumentId?.(documentId);
          }
          //richEdit.hasUnsavedChanges = false;
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.FileQuery.getVersionHistoryQueryKey(
              groupId!,
            ),
          });
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.FileQuery.getFileInfoQueryKey(documentId),
          });
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.FileQuery.searchFilesQueryKey({
              patientId: patientId,
            }),
          });
          if (blockId) {
            await queryClient.invalidateQueries({
              queryKey: QueryFactory.FileQuery.searchFilesQueryKey({
                dataBlockId: blockId,
              }),
            });
          }
          toast(i18n.t('file_saved_toast'), {
            type: 'success',
          });
          const isOpen = documentTemplateRules?.find(
            (x) => x.templateId === templateId,
          )?.isOpeningAfterSaving;
          if (isOpen) {
            await openOrDownloadFile(newVersion);
          }
        } catch (e) {
          await modals.showError({ text: convertToErrorString(e) });
        }
      }, DocumentFormatApi.OpenXml);
    },
  );

  return (
    <CustomButton
      {...props}
      buttonId={readyForPdfButtonId}
      onClick={handle}
      text={i18n.t('ready_for_pdf.button')}
      icon={'dxre-icon-ProtectDocument'}
    />
  );
};
